import type { ConfigType } from 'dayjs'
import { FieldTrigger } from '@/components/ui/field-trigger'
import { Calendar } from '@/components/ui/calendar'
import { getSafeDayjs } from '@/components/orders/delivery-share.utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import dayjs from '@/lib/dayjs'
import { cn, toCalendarDate, toYmdFromDate } from '@/lib/utils'
import { CalendarDays } from 'lucide-react'
import { useState } from 'react'

const DEFAULT_LABEL = 'Pick a date'

function toSafeCalendarDate(value: ConfigType): Date | null {
  const parsedDate = getSafeDayjs(value)

  return parsedDate ? parsedDate.startOf('day').add(12, 'hour').toDate() : null
}

function resolveVisibleMonth(selectedDate: Date | null): Date {
  return selectedDate || toCalendarDate(dayjs().startOf('month'))
}

type DeliveryRouteDatePickerProps = {
  value?: ConfigType
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
}

export function DeliveryRouteDatePicker({
  value,
  onChange,
  disabled = false,
  className = '',
}: DeliveryRouteDatePickerProps) {
  const selectedDate = toSafeCalendarDate(value)
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(() => resolveVisibleMonth(selectedDate))

  const handleOpenChange = (isOpen: boolean) => {
    setMonth(resolveVisibleMonth(selectedDate))
    setOpen(isOpen)
  }

  const handleSelect = (date: Date | undefined) => {
    if (!date) {
      return
    }

    onChange(toYmdFromDate(date))
    setOpen(false)
  }

  const label = getSafeDayjs(value)?.format('ddd, MMM D') ?? DEFAULT_LABEL

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <FieldTrigger
          placeholder={!value}
          className={cn('w-[180px] justify-start text-left', className)}
          disabled={disabled}
        >
          <CalendarDays className="mr-2 size-4" />
          <span>{label}</span>
        </FieldTrigger>
      </PopoverTrigger>
      <PopoverContent size="auto" padding="none" overflow="hidden" align="start">
        <Calendar
          mode="single"
          selected={selectedDate ?? undefined}
          captionLayout="dropdown-years"
          showOutsideDays={false}
          month={month}
          onMonthChange={setMonth}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  )
}

export default DeliveryRouteDatePicker
